import React, {useEffect, useState} from "react";
import axios from "axios";
import {Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Modal, Paper, Select, Switch, TextField, Tooltip} from "@mui/material";
import Typography from "@mui/material/Typography";
import {DataGrid, GridActionsCellItem, GridColDef} from "@mui/x-data-grid";
import AddBoxIcon from "@mui/icons-material/AddBox";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";
import {EventSite, PlotEvent} from "../../../server/Plot";
import {useTimezone} from "../contexts/TimezoneContext";
import {utcToDateInput, utcToTimeInput, localDateTimeToUtc} from "../utils/timezone";

const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 480,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

export default function EventManagement() {
    const tz = useTimezone()
    const [events, setEvents] = useState<PlotEvent[]>([]);
    const [sites, setSites] = useState<EventSite[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState<PlotEvent | null>(null);
    const [name, setName] = useState('');
    const [startDate, setStartDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endDate, setEndDate] = useState('');
    const [endTime, setEndTime] = useState('');
    const [maxTp, setMaxTp] = useState<string>('');
    const [paidParking, setPaidParking] = useState<boolean>(true);
    const [eventSiteId, setEventSiteId] = useState<number | ''>('');
    const [cmaEventId, setCmaEventId] = useState<string>('');
    const [cmaRegistrationId, setCmaRegistrationId] = useState<string>('');
    const [cmaScrapeEnabled, setCmaScrapeEnabled] = useState<boolean>(false);
    const [deleteTarget, setDeleteTarget] = useState<PlotEvent | null>(null);

    useEffect(() => {
        loadEvents();
        axios.get('/admin/sites').then(({data}) => setSites(Array.isArray(data) ? data : []));
    }, []);

    function loadEvents() {
        axios.get('/plot/events').then(({data}) => setEvents(Array.isArray(data) ? data : []));
    }

    function openCreate() {
        setEditingEvent(null);
        setName('');
        setStartDate('');
        setStartTime('');
        setEndDate('');
        setEndTime('');
        setMaxTp('');
        setPaidParking(true);
        setEventSiteId('');
        setCmaEventId('');
        setCmaRegistrationId('');
        setCmaScrapeEnabled(false);
        setModalOpen(true);
    }

    function openEdit(event: PlotEvent) {
        setEditingEvent(event);
        setName(event.name);
        setStartDate(utcToDateInput(event.start, tz));
        setStartTime(utcToTimeInput(event.start, tz));
        setEndDate(utcToDateInput(event.end, tz));
        setEndTime(utcToTimeInput(event.end, tz));
        setMaxTp(event.max_tp != null ? String(event.max_tp) : '');
        setPaidParking(event.paid_parking ?? true);
        setEventSiteId(event.eventSiteId ?? '');
        setCmaEventId(event.cma_event_id != null ? String(event.cma_event_id) : '');
        setCmaRegistrationId(event.cma_registration_id != null ? String(event.cma_registration_id) : '');
        setCmaScrapeEnabled(event.cma_scrape_enabled ?? false);
        setModalOpen(true);
    }

    function closeModal() {
        setModalOpen(false);
        setEditingEvent(null);
        setName('');
        setStartDate('');
        setStartTime('');
        setEndDate('');
        setEndTime('');
        setMaxTp('');
        setPaidParking(true);
        setEventSiteId('');
        setCmaEventId('');
        setCmaRegistrationId('');
        setCmaScrapeEnabled(false);
    }

    async function saveEvent() {
        const payload = {
            name,
            start: localDateTimeToUtc(startDate, startTime, tz),
            end: localDateTimeToUtc(endDate, endTime, tz),
            max_tp: maxTp !== '' ? parseInt(maxTp) : null,
            paid_parking: paidParking,
            eventSiteId: eventSiteId !== '' ? eventSiteId : null,
            cma_event_id: cmaEventId !== '' ? parseInt(cmaEventId) : null,
            cma_registration_id: cmaRegistrationId !== '' ? parseInt(cmaRegistrationId) : null,
            cma_scrape_enabled: cmaScrapeEnabled,
        };
        if (editingEvent) {
            await axios.put(`/admin/events/${editingEvent.id}`, payload).then(({data}) => {
                setEvents(prev => prev.map(e => e.id === data.id ? data : e));
            });
        } else {
            await axios.post('/admin/events', payload).then(({data}) => {
                setEvents(prev => [...prev, data]);
            });
        }
        closeModal();
    }

    async function confirmDelete() {
        if (!deleteTarget) return;
        await axios.delete(`/admin/events/${deleteTarget.id}`).then(() => {
            setEvents(prev => prev.filter(e => e.id !== deleteTarget.id));
            setDeleteTarget(null);
        });
    }

    async function unlockEvent(event: PlotEvent) {
        await axios.post(`/admin/events/${event.id}/unlock`);
        setEvents(prev => prev.map(e => e.id === event.id ? {...e, locked: false} : e));
    }

    const columns: GridColDef[] = [
        {field: 'name', headerName: 'Event Name', flex: 2},
        {
            field: 'start', headerName: 'Start', flex: 2,
            valueFormatter: (params: any) => params.value
                ? new Date(params.value).toLocaleString(undefined, {timeZone: tz}) : '—'
        },
        {
            field: 'end', headerName: 'End', flex: 2,
            valueFormatter: (params: any) => params.value
                ? new Date(params.value).toLocaleString(undefined, {timeZone: tz}) : '—'
        },
        {
            field: 'max_tp', headerName: 'Max TP', width: 90, align: 'center', headerAlign: 'center',
            valueFormatter: (params: any) => params.value ?? '—'
        },
        {
            field: 'locked', headerName: 'TP Status', width: 110, align: 'center', headerAlign: 'center',
            renderCell: (params: any) => params.row.locked
                ? <Tooltip title="Treasure Policy Locked"><Chip icon={<LockIcon fontSize="small"/>} label="Locked" color="warning" size="small"/></Tooltip>
                : null
        },
        {
            field: 'actions', type: 'actions', headerName: '', width: 110,
            getActions: (params) => [
                <GridActionsCellItem icon={<EditIcon/>} label="Edit" onClick={() => openEdit(params.row as PlotEvent)}/>,
                <GridActionsCellItem icon={<DeleteIcon/>} label="Delete" onClick={() => setDeleteTarget(params.row as PlotEvent)}/>,
                ...(params.row.locked ? [
                    <GridActionsCellItem icon={<LockOpenIcon/>} label="Unlock TP" showInMenu onClick={() => unlockEvent(params.row as PlotEvent)}/>
                ] : []),
            ]
        }
    ];

    return <React.Fragment>
        <Grid container spacing={1}>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h4" align="center">Event Management</Typography>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 1}}>
                        <Button variant="contained" startIcon={<AddBoxIcon/>} onClick={openCreate}>
                            New Event
                        </Button>
                    </Box>
                    <DataGrid
                        autoHeight
                        rows={events}
                        columns={columns}
                        getRowId={(row) => row.id}
                        density="compact"
                        initialState={{pagination: {paginationModel: {pageSize: 25}}}}
                    />
                </Paper>
            </Grid>
        </Grid>

        <Modal open={modalOpen} onClose={closeModal} aria-labelledby="event-form-title">
            <Box sx={modalStyle}>
                <Typography id="event-form-title" variant="h6" align="center" gutterBottom>
                    {editingEvent ? 'Edit Event' : 'New Event'}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Event Name" required value={name}
                                   onChange={ev => setName(ev.target.value)}/>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField fullWidth label={`Start Date (${tz})`} type="date" value={startDate}
                                   InputLabelProps={{shrink: true}}
                                   onChange={ev => setStartDate(ev.target.value)}/>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField fullWidth label="Start Time" type="time" value={startTime}
                                   InputLabelProps={{shrink: true}}
                                   onChange={ev => setStartTime(ev.target.value)}/>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField fullWidth label={`End Date (${tz})`} type="date" value={endDate}
                                   InputLabelProps={{shrink: true}}
                                   onChange={ev => setEndDate(ev.target.value)}/>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField fullWidth label="End Time" type="time" value={endTime}
                                   InputLabelProps={{shrink: true}}
                                   onChange={ev => setEndTime(ev.target.value)}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Max TP" type="number" value={maxTp}
                                   inputProps={{min: 0}}
                                   placeholder="Leave blank for no limit"
                                   InputLabelProps={{shrink: true}}
                                   onChange={ev => setMaxTp(ev.target.value)}/>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={<Switch checked={paidParking} onChange={ev => setPaidParking(ev.target.checked)}/>}
                            label="Paid Parking"/>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="event-site-label">Event Site</InputLabel>
                            <Select
                                labelId="event-site-label"
                                label="Event Site"
                                value={eventSiteId}
                                onChange={ev => setEventSiteId(ev.target.value as number | '')}>
                                <MenuItem value=""><em>None</em></MenuItem>
                                {sites.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField fullWidth label="CMA Event ID" type="number" value={cmaEventId}
                                   inputProps={{min: 1}}
                                   placeholder="Leave blank if not used"
                                   InputLabelProps={{shrink: true}}
                                   onChange={ev => setCmaEventId(ev.target.value)}/>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField fullWidth label="CMA Registration ID" type="number" value={cmaRegistrationId}
                                   inputProps={{min: 1}}
                                   placeholder="Leave blank if not used"
                                   InputLabelProps={{shrink: true}}
                                   onChange={ev => setCmaRegistrationId(ev.target.value)}/>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={<Switch checked={cmaScrapeEnabled} onChange={ev => setCmaScrapeEnabled(ev.target.checked)} disabled={!cmaEventId || !cmaRegistrationId}/>}
                            label="CMA Scraping Enabled (every 4 hrs)"/>
                    </Grid>
                    <Grid item xs={12} sx={{textAlign: 'center'}}>
                        <Button variant="contained" disabled={!name.trim()} onClick={saveEvent}>
                            {editingEvent ? 'Save' : 'Create'}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Modal>

        <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
            <DialogTitle>Delete Event</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Are you sure you want to delete "{deleteTarget?.name}"? Any plots assigned to this event will be unlinked.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
                <Button color="error" onClick={confirmDelete}>Delete</Button>
            </DialogActions>
        </Dialog>
    </React.Fragment>
}
